import React, { useState, useEffect } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@material-ui/core'
import usePersistedState from 'src/hooks/usePersistedState'
import stringToDate from 'src/utils/stringToDate'
import { PUBLISHED_STATUS } from 'src/components/NewsModule/constants'
import {
  getPost,
  getPostCategory,
  getPostTags,
  getTags
} from 'src/services/api/WebApi/NewsModule/newsModule'
import { ITag } from 'src/services/api/WebApi/NewsModule/interfaces'
import BreadcrumbsComponent from 'src/components/BreadcrumbsComponent/BreadcrumbsComponent'
import { AudienceNewsEnum } from 'src/types/news'
import ViewsNewsSkeleton from 'src/components/Skeleton/News/ViewsNewsSkeleton/ViewsNewsSkeleton'
import { useGlobalStyles } from 'src/mui/styles/globalStyles'
import { useStyles } from './ViewsNewsContent.styles'

interface RouteParams {
  id: number
}

const ViewNewsContent = () => {
  const location = useLocation()
  const urlParts = location.pathname.split('/')
  const urlCategory = urlParts[2]
  const urlTypeView = urlParts[3]
  const { id } = useParams() as unknown as RouteParams

  const { t } = useTranslation()
  const [user] = usePersistedState('auth-token', '')

  const [loading, setLoading] = useState(true)
  const [news, setNews] = useState<any>({})
  const [category, setCategory] = useState('')
  const [tags, setTags] = useState<ITag[]>([])

  const history = useHistory()

  const classesGlobal = useGlobalStyles()
  const classes = useStyles()

  const fetchTagList = async () => {
    try {
      const postTagsResponse = await getPostTags(id)
      const tagsResponse = await getTags()

      const postTags = postTagsResponse.data
      const tags = tagsResponse.data

      const aux = postTags
        .map((postTag) => tags.find((tag) => tag.id === postTag.tagId))
        .filter((tag) => tag !== undefined) as ITag[]

      setTags(aux)
    } catch (err) {
      console.log(err)
    }
  }

  const fetchNewsAndCategory = async () => {
    try {
      const response = await getPost(id)

      if (response.status === 404 || response.status === 400) {
        history.push('/notFound')
        return
      }

      const post = response.data
      setNews(post)

      try {
        const categoryResponse = await getPostCategory(post.newsCategoriesId)

        if (!user && post.audience === 'Interno') {
          history.push('/notFound')
          return
        }

        setCategory(categoryResponse.data.title)
        setLoading(false)
      } catch (err) {
        console.log(err)
      }
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    fetchNewsAndCategory()

    if (urlCategory !== 'careers') {
      fetchTagList()
    }
  }, [id, urlCategory])

  useEffect(() => {
    if (loading || !news || !category) return

    if (!user && news.audience === AudienceNewsEnum.Internal) {
      history.push('/notFound')
      return
    }

    if (urlTypeView === 'my-news' && news.userId !== parseInt(user?.id)) {
      history.push('/notFound')
      return
    }

    switch (category) {
      case 'Blog':
        if (urlCategory !== 'blog') {
          history.push('/notFound')
        }
        break
      case 'Vaga':
        if (urlCategory !== 'careers') {
          history.push('/notFound')
        }
        if (!user && news.status !== PUBLISHED_STATUS) {
          history.push('/notFound')
        }
        break
      case 'Publicação':
        if (urlCategory !== 'publications') {
          history.push('/notFound')
        }
        break
      case 'Defesa':
        if (urlCategory !== 'defenses') {
          history.push('/notFound')
        }
        break
      case 'Evento':
        if (urlCategory !== 'events') {
          history.push('/notFound')
        }
        break
      case 'Prêmio':
        if (urlCategory !== 'awards') {
          history.push('/notFound')
        }
        break
      default:
        break
    }
  }, [category, news, loading, user, urlCategory, urlTypeView, history])

  return (
    <>
      <Box className={classesGlobal.containerPages}>
        {loading ? (
          <ViewsNewsSkeleton />
        ) : (
          <Box style={{ width: '100%' }}>
            <BreadcrumbsComponent
              crumbs={[
                { label: t('navbar.start'), path: '/' },
                { label: t('news.title'), path: '/news' },
                {
                  label: t(`news.categories.${urlCategory}`),
                  path: `/news/${urlCategory}`
                },
                { label: news.title }
              ]}
            />
            <Box className={classes.containerContent}>
              <Typography className={classesGlobal.titleSectionVariant2}>
                {news.title}
              </Typography>
              {news.author && news.author !== 'null' && news.publishedAt ? (
                <Typography variant="body2" className={classes.writer}>{`${t(
                  'news.written-by'
                )} ${news.author} | ${stringToDate(
                  news.publishedAt
                )}`}</Typography>
              ) : (
                <></>
              )}
              <Box className={classes.imageContainer}>
                <img src={news.path} alt={news.alt} className={classes.image} />
                {news.alt ? (
                  <Typography variant="body2" className={classes.altImage}>
                    {news.alt}
                  </Typography>
                ) : (
                  <></>
                )}
              </Box>
              <Typography
                component="div"
                dangerouslySetInnerHTML={{
                  __html: news.text
                }}
              />
              <Box>
                <Typography className={classesGlobal.titleSectionVariant2}>
                  Tags
                </Typography>
                <Box className={classes.tagsContainer}>
                  {tags.map((data: any, key) => {
                    return (
                      <Typography key={key} className={classes.tags}>
                        {data.title}
                      </Typography>
                    )
                  })}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  )
}

export default ViewNewsContent
